import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Lightning, ArrowLeft, ChartBar, Clock, CheckCircle, Warning, Users, CalendarBlank, Timer } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const URGENCY_COLORS = { critical: "#dc2626", urgent: "#ea580c", moderate: "#d97706", minor: "#2563eb" };
const URGENCY_LABELS = { critical: "Critical", urgent: "Urgent", moderate: "Moderate", minor: "Minor" };
const STATUS_COLORS = { open: "#dc2626", acknowledged: "#d97706", in_progress: "#2563eb", completed: "#059669", cancelled: "#6b7280" };

export default function EmergencyAnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/emergency-jobs/analytics")
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-20 text-muted-foreground">Loading analytics...</div>;
  if (!data || data.total_jobs === 0) return (
    <div className="text-center py-20">
      <Lightning size={36} className="mx-auto text-muted-foreground/40 mb-3" />
      <p className="text-sm text-muted-foreground mb-3">No emergency jobs data yet</p>
      <Link to="/emergency-jobs" className="text-xs text-primary hover:underline">Go to Emergency Jobs</Link>
    </div>
  );

  const urgencyPieData = Object.entries(data.by_urgency || {}).map(([k, v]) => ({
    name: URGENCY_LABELS[k] || k, value: v.total, fill: URGENCY_COLORS[k] || "#6b7280",
  })).filter(d => d.value > 0);

  const statusPieData = Object.entries(data.by_status || {}).map(([k, v]) => ({
    name: k.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()), value: v, fill: STATUS_COLORS[k] || "#6b7280",
  })).filter(d => d.value > 0);

  const hourData = (data.busiest_hours || []).map(h => ({
    hour: `${String(h.hour).padStart(2, "0")}:00`, count: h.count,
  }));

  const dayData = data.busiest_days || [];

  return (
    <div data-testid="emergency-analytics-page">
      <div className="flex items-center gap-3 mb-5">
        <Link to="/emergency-jobs"><ArrowLeft size={18} className="text-muted-foreground hover:text-foreground" /></Link>
        <div className="h-9 w-9 rounded-lg bg-red-600 flex items-center justify-center">
          <ChartBar size={18} className="text-white" weight="fill" />
        </div>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] tracking-tight">Emergency Response Analytics</h1>
          <p className="text-xs text-muted-foreground">{data.total_jobs} total emergencies &middot; {data.active_jobs} active &middot; {data.completed_jobs} completed</p>
        </div>
        <Link to="/emergency-jobs/supplier-response" className="ml-auto">
          <Button variant="outline" size="sm" data-testid="supplier-response-btn"><Timer size={14} className="mr-1" /> Supplier Response</Button>
        </Link>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="analytics-kpi-cards">
        <KPICard label="Total Jobs" value={data.total_jobs} icon={Lightning} color="text-red-500" />
        <KPICard label="Active" value={data.active_jobs} icon={Warning} color="text-amber-500" />
        <KPICard label="Completed" value={data.completed_jobs} icon={CheckCircle} color="text-emerald-500" />
        <KPICard label="Avg SLA Rate" value={`${avgSlaRate(data.sla_compliance)}%`} icon={Clock} color="text-blue-500" />
      </div>

      {/* SLA Compliance */}
      <div className="border border-border rounded-lg p-5 mb-6" data-testid="sla-compliance-section">
        <h2 className="text-base font-semibold font-[Chivo] mb-4 flex items-center gap-2"><Clock size={16} className="text-primary" /> SLA Compliance by Urgency</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(URGENCY_LABELS).map(([key, label]) => {
            const sla = data.sla_compliance?.[key] || { total: 0, met: 0, breached: 0, rate: 0 };
            const resp = data.avg_response_times?.[key] || {};
            return (
              <div key={key} className="border border-border rounded-md p-4" data-testid={`sla-${key}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-bold text-white" style={{ background: URGENCY_COLORS[key] }}>{label}</span>
                  <span className="text-[10px] text-muted-foreground">{resp.target_hours}h target</span>
                </div>
                <div className="mb-2">
                  <p className="text-2xl font-bold font-[Chivo]" style={{ color: sla.rate >= 80 ? "#059669" : sla.rate >= 50 ? "#d97706" : "#dc2626" }}>
                    {sla.total > 0 ? `${sla.rate}%` : "—"}
                  </p>
                  <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Compliance</p>
                </div>
                {sla.total > 0 && <Progress value={sla.rate} className="h-1.5 mb-2" />}
                <div className="flex justify-between text-[10px]">
                  <span className="text-emerald-600">{sla.met} met</span>
                  <span className="text-red-600">{sla.breached} breached</span>
                </div>
                {resp.avg_hours !== null && resp.avg_hours !== undefined && (
                  <p className="text-[10px] text-muted-foreground mt-2 border-t border-border pt-2">
                    Avg response: <span className="font-semibold text-foreground">{resp.avg_hours}h</span>
                    {resp.min_hours !== null && <span className="ml-1">(min {resp.min_hours}h, max {resp.max_hours}h)</span>}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Urgency Distribution */}
        <div className="border border-border rounded-lg p-5" data-testid="urgency-chart">
          <h3 className="text-sm font-semibold font-[Chivo] mb-3">Urgency Distribution</h3>
          {urgencyPieData.length > 0 ? (
            <div className="flex items-center gap-6">
              <ResponsiveContainer width="50%" height={160}>
                <PieChart>
                  <Pie data={urgencyPieData} dataKey="value" cx="50%" cy="50%" outerRadius={60} innerRadius={35}>
                    {urgencyPieData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                  </Pie>
                  <Tooltip formatter={(v) => [v, "Jobs"]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2">
                {urgencyPieData.map(d => (
                  <div key={d.name} className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: d.fill }} />
                    <span className="text-xs">{d.name}</span>
                    <span className="text-xs font-bold ml-auto">{d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : <p className="text-xs text-muted-foreground">No data</p>}
        </div>

        {/* Status Distribution */}
        <div className="border border-border rounded-lg p-5" data-testid="status-chart">
          <h3 className="text-sm font-semibold font-[Chivo] mb-3">Status Breakdown</h3>
          {statusPieData.length > 0 ? (
            <div className="flex items-center gap-6">
              <ResponsiveContainer width="50%" height={160}>
                <PieChart>
                  <Pie data={statusPieData} dataKey="value" cx="50%" cy="50%" outerRadius={60} innerRadius={35}>
                    {statusPieData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                  </Pie>
                  <Tooltip formatter={(v) => [v, "Jobs"]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2">
                {statusPieData.map(d => (
                  <div key={d.name} className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: d.fill }} />
                    <span className="text-xs">{d.name}</span>
                    <span className="text-xs font-bold ml-auto">{d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : <p className="text-xs text-muted-foreground">No data</p>}
        </div>
      </div>

      {/* Busiest Times */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="border border-border rounded-lg p-5" data-testid="busiest-hours-chart">
          <h3 className="text-sm font-semibold font-[Chivo] mb-3 flex items-center gap-1.5"><CalendarBlank size={14} /> Busiest Hours</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={hourData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="hour" tick={{ fontSize: 9 }} interval={2} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip formatter={(v) => [v, "Emergencies"]} />
              <Bar dataKey="count" fill="#dc2626" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="border border-border rounded-lg p-5" data-testid="busiest-days-chart">
          <h3 className="text-sm font-semibold font-[Chivo] mb-3 flex items-center gap-1.5"><CalendarBlank size={14} /> Busiest Days</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={dayData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="day" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip formatter={(v) => [v, "Emergencies"]} />
              <Bar dataKey="count" fill="#ea580c" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top Suppliers + Categories */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Top Responding Suppliers */}
        <div className="border border-border rounded-lg overflow-hidden" data-testid="top-vendors-section">
          <div className="px-4 py-3 bg-muted/30 border-b border-border">
            <h3 className="text-sm font-semibold font-[Chivo] flex items-center gap-1.5"><Users size={14} /> Top Emergency Suppliers</h3>
          </div>
          {(data.top_vendors || []).length === 0 ? (
            <p className="px-4 py-6 text-xs text-muted-foreground text-center">No supplier data yet</p>
          ) : (
            <div className="divide-y divide-border">
              {(data.top_vendors || []).map((v, i) => (
                <div key={v.name} className="flex items-center justify-between px-4 py-3" data-testid={`top-vendor-${i}`}>
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] font-bold text-muted-foreground w-5">{i + 1}.</span>
                    <div>
                      <p className="text-sm font-medium">{v.name}</p>
                      <p className="text-[10px] text-muted-foreground">{v.total_jobs} jobs &middot; {v.completed} completed</p>
                    </div>
                  </div>
                  <div className="text-right">
                    {v.avg_response_hours !== null ? (
                      <>
                        <p className="text-sm font-bold font-mono">{v.avg_response_hours}h</p>
                        <p className="text-[9px] text-muted-foreground">avg response</p>
                      </>
                    ) : (
                      <span className="text-[10px] text-muted-foreground">—</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Category Breakdown */}
        <div className="border border-border rounded-lg overflow-hidden" data-testid="category-breakdown">
          <div className="px-4 py-3 bg-muted/30 border-b border-border">
            <h3 className="text-sm font-semibold font-[Chivo] flex items-center gap-1.5"><ChartBar size={14} /> Category Breakdown</h3>
          </div>
          {(data.category_breakdown || []).length === 0 ? (
            <p className="px-4 py-6 text-xs text-muted-foreground text-center">No data yet</p>
          ) : (
            <div className="divide-y divide-border">
              {(data.category_breakdown || []).map((c, i) => {
                const maxCount = data.category_breakdown[0]?.count || 1;
                return (
                  <div key={c.category} className="px-4 py-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium">{c.category}</span>
                      <span className="text-xs font-bold">{c.count}</span>
                    </div>
                    <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-red-500 rounded-full" style={{ width: `${(c.count / maxCount) * 100}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Monthly Trend */}
      {(data.monthly_trend || []).length > 0 && (
        <div className="border border-border rounded-lg p-5" data-testid="monthly-trend-chart">
          <h3 className="text-sm font-semibold font-[Chivo] mb-3">Monthly Trend</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.monthly_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="critical" stackId="a" fill="#dc2626" name="Critical" />
              <Bar dataKey="urgent" stackId="a" fill="#ea580c" name="Urgent" />
              <Bar dataKey="moderate" stackId="a" fill="#d97706" name="Moderate" />
              <Bar dataKey="minor" stackId="a" fill="#2563eb" name="Minor" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function KPICard({ label, value, icon: Icon, color }) {
  return (
    <div className="border border-border rounded-md p-3 bg-card" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <Icon size={18} className={`${color} mb-1.5`} weight="duotone" />
      <p className="text-xl font-bold font-[Chivo]">{value}</p>
      <p className="text-[9px] text-muted-foreground uppercase tracking-wider mt-0.5">{label}</p>
    </div>
  );
}

function avgSlaRate(sla) {
  if (!sla) return 0;
  const entries = Object.values(sla).filter(s => s.total > 0);
  if (entries.length === 0) return 0;
  return Math.round(entries.reduce((sum, s) => sum + s.rate, 0) / entries.length);
}
